import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FlatList, StyleSheet, View } from "react-native";
import { ComposerProps, GiftedChat, User } from "react-native-gifted-chat";
import GiftedChatAvatar from "./GiftedChatAvatar";
import GiftedChatBubble from "./GiftedChatBubble";
import GiftedChatComposer from "./GiftedChatComposer";
import GiftedChatDay from "./GiftedChatDay";
import GiftedChatEmptyView from "./GiftedChatEmptyView";
import GiftedChatInputToolbar from "./GiftedChatInputToolbar";
import GiftedChatMessage from "./GiftedChatMessage";
import GiftedChatSend from "./GiftedChatSend";
import GiftedMessageText from "./GiftedMessageText";
import SessionQuestions from "./SessionQuestions";
import {
  useCurrentConversation,
  useCurrentConversationMessages,
} from "./hooks";
import { usePostChatMessageMutation } from "../api/api";
import { useUser } from "../api/helpers";
import { useIsPresenting } from "../api/selectors";
import LightTheme from "../theme/LightTheme";

const Chats = () => {
  const [isQuestion, setIsQuestion] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const me = useUser();
  const user: User = useMemo(
    () => ({
      _id: 1,
      name: `${me?.firstName} ${me?.lastName}`,
      avatar: me?.avatarUrl,
    }),
    [me?.avatarUrl, me?.firstName, me?.lastName],
  );

  const conversation = useCurrentConversation();
  const messages = useCurrentConversationMessages();
  const [postChatMessage, { isLoading }] = usePostChatMessageMutation();

  const flatListRef = useRef<FlatList>();

  useEffect(() => {
    const wheelEventHandler = (e: WheelEvent) => {
      scrollNode.scrollTop -= e.deltaY;
      e.preventDefault();
    };
    const scrollNode = flatListRef.current?.getScrollableNode();
    scrollNode?.addEventListener("wheel", wheelEventHandler);
    return () => scrollNode?.removeEventListener("wheel", wheelEventHandler);
  }); // needs to run any time flatlist mounts

  const handleQuestionChange = useCallback(() => {
    setIsQuestion((value) => !value);
  }, []);

  const onSend = useCallback(() => {
    if (conversation) {
      postChatMessage({
        isQuestion,
        message: text,
        topic: conversation.topic,
      })
        .unwrap()
        .then(() => {
          setText("");
        });
    }
  }, [conversation, isQuestion, postChatMessage, text]);

  const isPresenting = useIsPresenting();

  const renderComposer = useCallback(
    (composerProps: ComposerProps) => (
      <GiftedChatComposer
        composerProps={composerProps}
        text={text}
        onSend={onSend}
      />
    ),
    [onSend, text],
  );

  const listViewProps = useMemo(
    () => ({
      ref: flatListRef,
      style: {
        marginBottom: "10px",
      },
    }),
    [],
  );

  const extraData = useMemo(() => ({ isLoading }), [isLoading]);

  return (
    <>
      {isPresenting && (
        <div className="sessionQuestions">
          <SessionQuestions />
        </div>
      )}
      <div className="chatWrapper">
        <View style={styles.container}>
          <GiftedChat
            alwaysShowSend
            messages={messages}
            text={text}
            onInputTextChanged={setText}
            onSend={onSend}
            user={user}
            inverted
            renderMessage={GiftedChatMessage}
            renderMessageText={GiftedMessageText}
            renderInputToolbar={GiftedChatInputToolbar}
            placeholder="Start typing..."
            renderSend={GiftedChatSend}
            renderBubble={GiftedChatBubble}
            renderDay={GiftedChatDay}
            renderAvatar={GiftedChatAvatar}
            renderChatEmpty={GiftedChatEmptyView}
            renderAvatarOnTop
            renderComposer={renderComposer}
            showUserAvatar
            listViewProps={listViewProps}
            extraData={extraData}
          />
          <div className="isQuestionBlock">
            <input
              type="checkbox"
              checked={isQuestion}
              onChange={handleQuestionChange}
            />
            <h4 className="checkboxLabel">
              Send question directly to presenter.
            </h4>
          </div>
        </View>
      </div>
      <style jsx>
        {`
          .sessionQuestions {
            display: flex;
            overflow: hidden;
            max-height: 50%;
          }
          .chatWrapper {
            width: 100%;
            display: flex;
            flex: 1 1 auto;
          }
          .isQuestionBlock {
            display: flex;
            padding: 15px 0 0 20px;
            font-weight: normal;
            font-size: 0.8rem;
            color: ${LightTheme.font.secondary.color};
            align-items: center;
          }
          input[type="checkbox"] {
            margin-right: 14px;
          }
        `}
      </style>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
    paddingLeft: 10,
  },
});

export default Chats;
